import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * /what-we-do — strategic, to-the-point.
 * 6 sections: hero · industry+stats · 4 problems · 3 pillars · 4 differentiators · CTA.
 *
 * All illustrations are pure SVG with CSS keyframes, perfectly centered on a
 * single focal cognitive core. No external animation libs.
 */

/* ───────── Illustration: Cognitive Orb (Hero) ─────────
 * Atomic-style orbital diagram using SVG-native SMIL <animateTransform>.
 * Three concentric CIRCULAR orbits (not ellipses, so the nodes truly travel
 * along them). 4 nodes total, each on its own ring, rotating at its own speed.
 * SMIL guarantees rotation happens around the exact SVG coordinate origin —
 * no CSS transform-origin/transform-box browser inconsistencies.
 */
function CognitiveOrb() {
  const orbits = [
    { r: 200, dur: 32, dir: 1, nodes: [{ a: 0, color: "var(--ember)", size: 6 }] },
    { r: 150, dur: 22, dir: -1, nodes: [
      { a: 30, color: "var(--steel-300)", size: 5 },
      { a: 210, color: "var(--ember)", size: 4 },
    ] },
    { r: 100, dur: 16, dir: 1, nodes: [{ a: 90, color: "var(--steel-300)", size: 4 }] },
  ];

  return (
    <svg viewBox="-260 -260 520 520" className="wwd-orb" aria-hidden="true">
      <defs>
        <radialGradient id="orbHalo" cx="0" cy="0" r="240" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="var(--ember)" stopOpacity="0.18" />
          <stop offset="70%" stopColor="var(--ember)" stopOpacity="0.02" />
          <stop offset="100%" stopColor="var(--ember)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Ambient halo */}
      <circle r="240" fill="url(#orbHalo)" />

      {/* Static orbital rings (do NOT rotate — they're guides) */}
      <g fill="none" stroke="var(--steel-300)" opacity="0.35">
        {orbits.map((o) => (
          <circle key={o.r} r={o.r} strokeWidth="0.7" strokeDasharray="2 6" />
        ))}
      </g>

      {/* Orbiting nodes — each ring rotates as a group, nodes pre-positioned via translate */}
      {orbits.map((orbit, oi) => (
        <g key={oi}>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={orbit.dir > 0 ? "0" : "360"}
            to={orbit.dir > 0 ? "360" : "0"}
            dur={`${orbit.dur}s`}
            repeatCount="indefinite"
          />
          {orbit.nodes.map((n, ni) => {
            const rad = (n.a * Math.PI) / 180;
            const x = Math.cos(rad) * orbit.r;
            const y = Math.sin(rad) * orbit.r;
            return (
              <g key={ni} transform={`translate(${x.toFixed(2)} ${y.toFixed(2)})`}>
                <circle r={n.size + 5} fill="none" stroke={n.color} strokeWidth="0.8" opacity="0.55">
                  <animate attributeName="r" values={`${n.size};${n.size * 2.4};${n.size * 2.4}`} dur="2.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.75;0;0" dur="2.6s" repeatCount="indefinite" />
                </circle>
                <circle r={n.size} fill={n.color} />
              </g>
            );
          })}
        </g>
      ))}

      {/* Central cognitive core (no rotation — just breathing) */}
      <g>
        <circle r="36" fill="none" stroke="var(--ember)" strokeWidth="0.8" opacity="0">
          <animate attributeName="r" values="22;42;42" dur="3.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0;0" dur="3.6s" repeatCount="indefinite" />
        </circle>
        <circle r="14" fill="var(--ember)">
          <animate attributeName="r" values="14;17;14" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle r="4" fill="#fff" />
      </g>

      {/* Floating data tags — corners of the canvas, fade in/out sequentially */}
      <g fontFamily="var(--f-mono, monospace)" fontSize="10" fill="var(--text-3)" letterSpacing="1.6">
        <text x="-30" y="-228" opacity="0">
          $106T
          <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.15;0.35;0.5" dur="8s" repeatCount="indefinite" />
        </text>
        <text x="180" y="-100" opacity="0">
          EPC
          <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.15;0.35;0.5" dur="8s" begin="2s" repeatCount="indefinite" />
        </text>
        <text x="160" y="225" opacity="0">
          2M DOCS
          <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.15;0.35;0.5" dur="8s" begin="4s" repeatCount="indefinite" />
        </text>
        <text x="-200" y="120" opacity="0">
          FEED→COMM
          <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.15;0.35;0.5" dur="8s" begin="6s" repeatCount="indefinite" />
        </text>
      </g>
    </svg>
  );
}

/* ───────── Illustration: Cognitive Constellation (Industry) ─────────
 * 5 infrastructure icons on a radial pattern around a centered CORE.
 * Uses SMIL <animateTransform> for the icon float and blade rotation —
 * guaranteed centered, no transform-origin ambiguity.
 */
function CognitiveConstellation() {
  const angles = [-90, -22, 50, 130, 210];
  const R = 170;
  const positions = angles.map((deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: Math.cos(rad) * R, y: Math.sin(rad) * R };
  });

  const icons = [
    /* 0 — Wind turbine (with spinning blades around hub at 0,2) */
    (
      <g key="wind" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round">
        <line x1="0" y1="2" x2="0" y2="22" />
        <g>
          <animateTransform attributeName="transform" type="rotate" from="0 0 2" to="360 0 2" dur="3s" repeatCount="indefinite" />
          <line x1="0" y1="2" x2="-12" y2="-10" />
          <line x1="0" y1="2" x2="14" y2="-8" />
          <line x1="0" y1="2" x2="-4" y2="18" />
        </g>
        <circle cx="0" cy="2" r="2.4" fill="var(--ember)" stroke="none" />
      </g>
    ),
    /* 1 — Datacenter racks */
    (
      <g key="dc" stroke="currentColor" strokeWidth="1.5" fill="none">
        <rect x="-16" y="-12" width="32" height="8" rx="1" />
        <rect x="-16" y="-2" width="32" height="8" rx="1" />
        <rect x="-16" y="8" width="32" height="8" rx="1" />
        <circle cx="-12" cy="-8" r="1.4" fill="var(--ember)" stroke="none" />
        <circle cx="-12" cy="2" r="1.4" fill="var(--ember)" stroke="none" />
        <circle cx="-12" cy="12" r="1.4" fill="var(--ember)" stroke="none" />
      </g>
    ),
    /* 2 — Refinery */
    (
      <g key="ref" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round">
        <path d="M-20 14 L-20 -8 L-12 -14 L-4 -8 L-4 14 Z" />
        <path d="M0 14 L0 -16 L8 -22 L16 -16 L16 14" />
        <path d="M-22 14 L20 14" />
        <circle cx="-16" cy="-2" r="1.4" fill="var(--ember)" stroke="none" />
      </g>
    ),
    /* 3 — Bridge */
    (
      <g key="br" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round">
        <path d="M-22 8 L22 8" />
        <path d="M-22 8 L-12 -6 L-2 8 L8 -6 L18 8" />
        <line x1="-12" y1="8" x2="-12" y2="14" />
        <line x1="8" y1="8" x2="8" y2="14" />
      </g>
    ),
    /* 4 — LNG tank */
    (
      <g key="lng" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round">
        <ellipse cx="0" cy="10" rx="18" ry="4" />
        <path d="M-18 10 L-18 -4 Q0 -12 18 -4 L18 10" />
        <circle cx="-8" cy="-2" r="1.4" fill="var(--ember)" stroke="none" />
      </g>
    ),
  ];

  return (
    <svg viewBox="-240 -240 480 480" className="wwd-cc" aria-hidden="true">
      <defs>
        <linearGradient id="ccLine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--ember)" stopOpacity="0" />
          <stop offset="50%" stopColor="var(--ember)" stopOpacity="0.95" />
          <stop offset="100%" stopColor="var(--ember)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ccLineSteel" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--steel-300)" stopOpacity="0" />
          <stop offset="50%" stopColor="var(--steel-300)" stopOpacity="0.85" />
          <stop offset="100%" stopColor="var(--steel-300)" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="ccGlow" r="240" cx="0" cy="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="var(--ember)" stopOpacity="0.14" />
          <stop offset="70%" stopColor="var(--ember)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Soft halo */}
      <circle r="240" fill="url(#ccGlow)" />

      {/* Static orbit guide ring */}
      <circle r="170" fill="none" stroke="var(--border-strong)" strokeWidth="0.6" strokeDasharray="2 6" opacity="0.45" />

      {/* Connecting lines (icon → core) — animated flow */}
      <g fill="none" strokeWidth="1.4" strokeLinecap="round">
        {positions.map((p, i) => (
          <line
            key={"l" + i}
            x1={(p.x * 0.78).toFixed(2)}
            y1={(p.y * 0.78).toFixed(2)}
            x2={(p.x * 0.18).toFixed(2)}
            y2={(p.y * 0.18).toFixed(2)}
            stroke={i % 2 === 0 ? "url(#ccLine)" : "url(#ccLineSteel)"}
            strokeDasharray="60 140"
            strokeDashoffset="200"
          >
            <animate attributeName="stroke-dashoffset" from="200" to="0" dur="3.2s" begin={`${i * 0.6}s`} repeatCount="indefinite" />
          </line>
        ))}
      </g>

      {/* Floating icon nodes — each floats vertically via SMIL translate */}
      {positions.map((p, i) => (
        <g key={"n" + i} transform={`translate(${p.x.toFixed(2)} ${p.y.toFixed(2)})`}>
          <g>
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0 0; 0 -6; 0 0"
              dur="4.5s"
              begin={`${i * 0.4}s`}
              repeatCount="indefinite"
            />
            <circle r="32" fill="var(--bg)" stroke="var(--border-strong)" strokeWidth="0.8" opacity="0.95" />
            <g style={{ color: "var(--text)" }}>{icons[i]}</g>
          </g>
        </g>
      ))}

      {/* Central core */}
      <g>
        <circle r="48" fill="none" stroke="var(--ember)" strokeWidth="0.7" opacity="0">
          <animate attributeName="r" values="32;56;56" dur="3.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.55;0;0" dur="3.6s" repeatCount="indefinite" />
        </circle>
        <circle r="32" fill="none" stroke="var(--ember)" strokeWidth="1" opacity="0.85" />
        <circle r="20" fill="var(--ember)" />
        <text y="3" textAnchor="middle" fontSize="8" letterSpacing="1.8" fontFamily="var(--f-mono, monospace)" fill="#fff" fontWeight="600">CORE</text>
      </g>
    </svg>
  );
}

/* ───────── CountUp helper ───────── */
function CountUp({ end, decimals = 0, prefix = "", suffix = "", duration = 1600 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const display = decimals ? val.toFixed(decimals) : Math.round(val);
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

/* ───────── Section data — strategic, to the point ───────── */
const STATS = [
  { v: 106, prefix: "$", suffix: "T", lbl: "Global infrastructure investment through 2040" },
  { v: 12.3, prefix: "$", suffix: "T", decimals: 1, lbl: "Annual global construction market" },
  { v: 1.6, prefix: "$", suffix: "T", decimals: 1, lbl: "Wasted yearly on overruns and delays" },
  { v: 77, suffix: "%", lbl: "Of megaprojects exceed their original budget" },
];

const PROBLEMS = [
  {
    n: "01",
    title: "Wrong-Revision Rework",
    stat: "$5–15M lost per project",
    body: "A piping engineer on-site opens a P&ID to fabricate a spool — and it's Rev-B, not Rev-C. The nozzle orientation changed two weeks ago. By the time anyone catches it, the steel is cut, the welds are done, and $800K of fabrication is scrap. This happens on every project, every month, worldwide — because the document management system doesn't know which drawing is current, which tag it belongs to, or who approved the latest revision.",
    cost: "$5–15M in rework per $500M project. 18% of engineering hours spent searching, not engineering.",
  },
  {
    n: "02",
    title: "Blind Capital Decisions",
    stat: "$50–200K and 14 weeks to answer one question",
    body: "A Chief Projects Officer needs to know: \"Are we ready to execute the next $500M program?\" The answer requires hiring a consulting firm, mobilizing 8 consultants for 14 weeks, and producing a deck that's outdated before the ink dries. The diagnostic costs $50–200K and can only be repeated every 3–5 years — meaning capital allocation decisions worth hundreds of millions are made on evidence that's years old.",
    cost: "Billions committed on stale capability data. Transformation programs scoped without a current baseline.",
  },
  {
    n: "03",
    title: "Seven Systems, Zero Coherence",
    stat: "77% of megaprojects over budget. 89% late.",
    body: "Schedule lives in Primavera. Cost in SAP. Documents in SharePoint. Procurement in Oracle. Risk in someone's notebook. Quality in a parallel universe. When the project director asks \"are we on track?\", answering requires pulling data from seven systems, reconciling it in Excel, and presenting it three days later — by which time the answer has changed. No single platform connects engineering, procurement, construction, and controls. So nobody sees the whole project until the post-mortem.",
    cost: "$1.6 trillion wasted globally on overruns every year. Most discovered too late to correct.",
  },
  {
    n: "04",
    title: "The Procurement Black Box",
    stat: "60–70% of project cost, least sophisticated tooling",
    body: "On a $1B capital project, $600–700M flows through procurement — yet the sourcing pipeline runs on RFQ spreadsheets, bid evaluations happen over email, and contract variations are tracked in personal files. Nobody can answer \"what's our total commercial exposure?\" without two weeks of manual data gathering. Claims go unrecovered. LDs go untracked. Savings from framework agreements go unmeasured. The function that controls the majority of project cost operates with less data visibility than the company canteen.",
    cost: "3–8% cost leakage on every project. On a $1B program, that's $30–80M walking out the door unnoticed.",
  },
];

const PILLARS = [
  {
    n: "01",
    tag: "Diagnose",
    title: "Know where you stand. Know where to go.",
    body: "AI-powered self-service diagnostics that replace 14-week, $200K consulting engagements with 5-day, single-token assessments.",
    products: [
      { slug: "assayo", name: "Assayo", role: "Maturity diagnostics · 17 industry templates" },
      { slug: "evalore", name: "Evalore", role: "Platform evaluation · evidence-based scoring" },
      { slug: "luminex", name: "Luminex", role: "Process re-engineering · 7,400+ process library" },
    ],
  },
  {
    n: "02",
    tag: "Engineer",
    title: "One source of truth. Intelligent from core.",
    body: "AI-native systems built for capital-project engineering — native DWG/IFC viewing, tag-aware P&ID intelligence, predictive earned-value analysis.",
    products: [
      { slug: "docuweave", name: "Docuweave", role: "Document management · tag-aware EDMS" },
      { slug: "formigen", name: "Formigen", role: "AI-assisted Engineering & Design from Concept till Detailed Engineering" },
      { slug: "panorix", name: "Panorix", role: "Project controls · predictive EVM" },
    ],
  },
  {
    n: "03",
    tag: "Procure",
    title: "The commercial thread, end to end.",
    body: "Full source-to-pay across 9 sourcing event types, FIDIC/NEC-aware contract management, AI clause analysis — for the 60–70% of cost that flows through procurement.",
    products: [
      { slug: "procurex", name: "ProcureX", role: "Procurement & contracts · S2P + FIDIC AI" },
    ],
  },
];

const DIFFERENTIATORS = [
  {
    n: "01",
    title: "Domain-native AI, not a generic wrapper",
    body: "Built on EPC methodologies, FIDIC/NEC clause structures, ISA 5.1, CFIHOS, ISO 19650. Outputs a Big-4 Principal recognises as methodologically sound.",
  },
  {
    n: "02",
    title: "Eight platforms, one cognitive substrate",
    body: "Competitors are point solutions. Capexara's 8 platforms share one data backbone, one design system, one reasoning engine. A change in one is visible to every other.",
  },
  {
    n: "03",
    title: "Generative AI in every workflow",
    body: "Not bolted on. Assayo writes roadmaps. ProcureX analyses clauses. Panorix predicts delays. Cognara reasons across all of it.",
  },
  {
    n: "04",
    title: "Consumption pricing. No $500K licenses.",
    body: "Start with one assessment token. Scale to full platform deployment without a procurement cycle. Pay for outcomes, not seats.",
  },
];

export default function WhatWeDo() {
  return (
    <article className="wwd" data-testid="what-we-do-page">
      {/* ───────── 1 · HERO ───────── */}
      <section className="wwd-hero" data-testid="wwd-hero">
        <div className="container wwd-hero-inner">
          <div className="wwd-hero-copy">
            <nav className="wwd-crumb mono" aria-label="Breadcrumb">
              <Link to="/">Home</Link> <span>/</span> <span>What we do</span>
            </nav>
            <div className="section-label" style={{ marginTop: 24 }}>
              <span className="dot" />What we do
            </div>
            <h1 className="wwd-h1 display" data-testid="wwd-h1">
              AI for the industry that <em>builds the physical world.</em>
            </h1>
            <p className="wwd-lede">
              Refineries, power plants, metros, LNG, data centres — Capexara AI is the cognitive substrate beneath the largest category of global investment. Engineer smarter. Procure faster. Deliver on time.
            </p>
            <div className="wwd-cta-row">
              <Link to="/platforms" className="btn btn-primary" data-testid="wwd-cta-platforms">
                See our platforms <span className="arrow">→</span>
              </Link>
              <Link to="/contact" className="btn btn-ghost" data-testid="wwd-cta-demo">
                Book a demo
              </Link>
            </div>
          </div>
          <div className="wwd-hero-art" aria-hidden="true">
            <CognitiveOrb />
          </div>
        </div>
      </section>

      {/* ───────── 2 · INDUSTRY + STATS ───────── */}
      <section className="wwd-section wwd-industry" data-testid="wwd-industry">
        <div className="container">
          <div className="wwd-industry-grid">
            <div className="wwd-industry-copy">
              <div className="section-label">
                <span className="dot" />The industry
              </div>
              <h2 className="wwd-h2 display">
                Capital projects are how the world gets <em>built.</em>
              </h2>
              <p className="wwd-prose-p">
                3 to 12 years. 10 to 50 engineering disciplines. Up to 2 million documents per project. Hundreds of vendors across multiple countries. Billions of dollars on decisions made under uncertainty.
              </p>
              <p className="wwd-prose-p">
                It's not generic enterprise. It's not horizontal business process. It's a specific, complex, high-stakes world — and the one Capexara AI was built for.
              </p>
            </div>
            <div className="wwd-industry-art" aria-hidden="true">
              <CognitiveConstellation />
            </div>
          </div>

          <div className="wwd-stats" data-testid="wwd-stats">
            {STATS.map((s, i) => (
              <div className="wwd-stat reveal" key={i} style={{ "--d": `${i * 80}ms` }}>
                <div className="wwd-stat-v">
                  <CountUp end={s.v} prefix={s.prefix || ""} suffix={s.suffix || ""} decimals={s.decimals || 0} />
                </div>
                <div className="wwd-stat-l mono">{s.lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── 3 · PROBLEMS ───────── */}
      <section className="wwd-section wwd-section--alt" data-testid="wwd-problems">
        <div className="container">
          <div className="section-label">
            <span className="dot" />The problems
          </div>
          <h2 className="wwd-h2 display">
            Four failures costing the industry <em>trillions every year.</em>
          </h2>

          <div className="wwd-problems">
            {PROBLEMS.map((p, i) => (
              <article className="wwd-problem reveal" key={p.n} style={{ "--d": `${i * 100}ms` }} data-testid={`wwd-problem-${p.n}`}>
                <div className="wwd-problem-bar" aria-hidden="true" />
                <div className="wwd-problem-n mono">{p.n}</div>
                <div className="wwd-problem-body">
                  <h3 className="wwd-problem-title">{p.title}</h3>
                  <div className="wwd-problem-stat mono">{p.stat}</div>
                  <p className="wwd-problem-text">{p.body}</p>
                </div>
                <div className="wwd-problem-cost">
                  <span className="mono">Cost</span>
                  <span>{p.cost}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── 4 · PILLARS ───────── */}
      <section className="wwd-section" data-testid="wwd-solution">
        <div className="container">
          <div className="section-label">
            <span className="dot" />What we do
          </div>
          <h2 className="wwd-h2 display">
            Three capabilities. One cognitive layer. <em>Whole lifecycle.</em>
          </h2>

          <div className="wwd-pillars">
            {PILLARS.map((pillar, i) => (
              <article className="wwd-pillar reveal" key={pillar.n} style={{ "--d": `${i * 120}ms` }} data-testid={`wwd-pillar-${pillar.n}`}>
                <header className="wwd-pillar-head">
                  <span className="wwd-pillar-n mono">{pillar.n}</span>
                  <span className="wwd-pillar-tag mono">{pillar.tag}</span>
                </header>
                <h3 className="wwd-pillar-title display">{pillar.title}</h3>
                <p className="wwd-pillar-body">{pillar.body}</p>
                <ul className="wwd-pillar-products">
                  {pillar.products.map((prod) => (
                    <li key={prod.slug}>
                      <Link to={`/platforms/${prod.slug}`} className="wwd-pp" data-testid={`wwd-product-${prod.slug}`}>
                        <span className="wwd-pp-name">{prod.name}</span>
                        <span className="wwd-pp-role">{prod.role}</span>
                        <span className="wwd-pp-arrow" aria-hidden="true">→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          {/* Cognara connecting block — punchier */}
          <div className="wwd-sutra reveal" data-testid="wwd-sutra">
            <div className="wwd-sutra-glow" aria-hidden="true" />
            <div className="wwd-sutra-mark mono">Connecting all three</div>
            <h3 className="wwd-sutra-name display">
              Cognara <span className="wwd-sutra-dev"></span>
            </h3>
            <p className="wwd-sutra-body">
              The generative AI reasoning engine. Ask in plain language: <em>"Which documents linked to tag FE-4421 have been revised since the last transmittal?"</em> Cognara synthesises the answer across documents, schedules, procurement and assessments — simultaneously.
            </p>
            <Link to="/platforms/cognara" className="wwd-sutra-cta" data-testid="wwd-sutra-link">
              Meet Cognara <span className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── 5 · DIFFERENTIATORS ───────── */}
      <section className="wwd-section wwd-section--alt" data-testid="wwd-diff">
        <div className="container">
          <div className="section-label">
            <span className="dot" />How we're different
          </div>
          <h2 className="wwd-h2 display">
            Four things <em>no other platform does.</em>
          </h2>

          <ol className="wwd-diff-list">
            {DIFFERENTIATORS.map((d, i) => (
              <li className="wwd-diff-row reveal" key={d.n} style={{ "--d": `${i * 90}ms` }} data-testid={`wwd-diff-${d.n}`}>
                <div className="wwd-diff-n display">{d.n}</div>
                <div className="wwd-diff-copy">
                  <h3 className="wwd-diff-title">{d.title}</h3>
                  <p className="wwd-diff-body">{d.body}</p>
                </div>
                <div className="wwd-diff-rule" aria-hidden="true" />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ───────── Final CTA section removed per user request ───────── */}

      {/* ───────── Scoped styles ───────── */}
      <style>{`
        .wwd { background: var(--bg); color: var(--text); }
        .wwd em { font-style: italic; color: var(--text-3); }
        [data-theme="dark"] .wwd em { color: var(--steel-200); }

        .wwd-section { padding: 96px 0; position: relative; }
        .wwd-section--alt { background: var(--bg-sunk); }
        [data-theme="dark"] .wwd-section--alt { background: var(--bg-band); }

        .wwd-h1 {
          margin: 16px 0 22px;
          font-size: clamp(38px, 5vw, 64px);
          line-height: 1.05; letter-spacing: -0.022em;
          font-weight: 400; max-width: 720px;
        }
        .wwd-h2 {
          margin: 14px 0 36px;
          font-size: clamp(28px, 3.8vw, 48px);
          line-height: 1.08; letter-spacing: -0.02em;
          font-weight: 400; max-width: 900px;
        }

        /* ───────── 1 · Hero ───────── */
        .wwd-hero {
          padding: 88px 0 72px;
          position: relative;
          border-bottom: 1px solid var(--border);
          overflow: hidden;
        }
        .wwd-hero-inner {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 48px;
          align-items: center;
          min-height: 480px;
        }
        .wwd-hero-art {
          width: 100%;
          aspect-ratio: 1 / 1;
          max-width: 560px;
          margin: 0 auto;
          animation: wwdFadeIn 1s ease both;
        }
        .wwd-crumb { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--text-3); }
        .wwd-crumb a { color: var(--text-3); text-decoration: none; transition: color .2s ease; }
        .wwd-crumb a:hover { color: var(--ember); }
        .wwd-crumb span { margin: 0 8px; opacity: 0.5; }
        .wwd-lede {
          max-width: 560px;
          font-size: 18px;
          line-height: 1.55;
          color: var(--text-2);
          margin: 0 0 28px;
        }
        .wwd-cta-row { display: flex; flex-wrap: wrap; gap: 12px; }

        /* ───────── 2 · Industry + Stats ───────── */
        .wwd-industry-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
          gap: 48px;
          align-items: center;
          margin-bottom: 64px;
        }
        .wwd-industry-art {
          width: 100%;
          aspect-ratio: 1 / 1;
          max-width: 500px;
          margin: 0 auto;
        }
        .wwd-prose-p {
          font-size: 17px;
          line-height: 1.6;
          color: var(--text-2);
          margin: 0 0 18px;
          max-width: 560px;
        }
        .wwd-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          border-top: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
        }
        .wwd-stat {
          padding: 28px 24px;
          border-right: 1px solid var(--border);
        }
        .wwd-stat:last-child { border-right: none; }
        .wwd-stat-v {
          font-family: var(--f-display);
          font-size: clamp(32px, 3.8vw, 52px);
          line-height: 1;
          letter-spacing: -0.025em;
          color: var(--text);
          margin-bottom: 10px;
          font-variant-numeric: tabular-nums;
          font-weight: 400;
        }
        .wwd-stat-l {
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--text-3);
          line-height: 1.5;
        }

        /* ───────── 3 · Problems ───────── */
        .wwd-problems {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0;
          border-top: 1px solid var(--border);
          border-left: 1px solid var(--border);
        }
        .wwd-problem {
          position: relative;
          display: grid;
          grid-template-columns: auto 1fr;
          column-gap: 24px;
          padding: 32px 32px 28px;
          border-right: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          transition: background-color .35s ease;
          overflow: hidden;
        }
        .wwd-problem-bar {
          position: absolute; left: 0; top: 0; bottom: 0;
          width: 2px; background: var(--ember);
          transform: scaleY(0); transform-origin: top;
          transition: transform .45s cubic-bezier(.2,.8,.2,1);
        }
        .wwd-problem:hover { background: var(--bg-sunk); }
        [data-theme="dark"] .wwd-problem:hover { background: rgba(255,255,255,0.03); }
        .wwd-problem:hover .wwd-problem-bar { transform: scaleY(1); }
        .wwd-problem-n {
          font-size: 11px;
          letter-spacing: 0.24em;
          color: var(--ember);
          padding-top: 6px;
        }
        .wwd-problem-title {
          font-family: var(--f-display);
          font-size: 22px;
          line-height: 1.2;
          letter-spacing: -0.01em;
          margin: 0 0 8px;
          font-weight: 500;
          color: var(--text);
        }
        .wwd-problem-stat {
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--ember);
          margin-bottom: 14px;
        }
        .wwd-problem-text {
          font-size: 14.5px;
          line-height: 1.6;
          color: var(--text-2);
          margin: 0;
        }
        .wwd-problem-cost {
          grid-column: 1 / -1;
          margin-top: 16px;
          padding-top: 14px;
          border-top: 1px dashed var(--border);
          display: flex;
          gap: 14px;
          font-size: 13px;
          color: var(--text-3);
          font-style: italic;
        }
        .wwd-problem-cost .mono {
          font-style: normal;
          color: var(--ember);
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          padding-top: 2px;
        }

        /* ───────── 4 · Pillars ───────── */
        .wwd-pillars {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .wwd-pillar {
          position: relative;
          padding: 36px 32px 32px;
          border: 1px solid var(--border);
          background: var(--bg-sunk);
          overflow: hidden;
          transition: transform .35s cubic-bezier(.2,.8,.2,1), border-color .35s ease;
        }
        [data-theme="dark"] .wwd-pillar { background: rgba(255,255,255,0.02); }
        .wwd-pillar::before {
          content: ""; position: absolute; left: 0; top: 0;
          width: 3px; height: 100%;
          background: linear-gradient(180deg, var(--ember) 0%, transparent 100%);
          transform: scaleY(0.25); transform-origin: top;
          transition: transform .5s cubic-bezier(.2,.8,.2,1);
        }
        .wwd-pillar:hover { transform: translateY(-3px); border-color: var(--ember); }
        .wwd-pillar:hover::before { transform: scaleY(1); }
        .wwd-pillar-head {
          display: flex; align-items: center; gap: 14px;
          margin-bottom: 18px;
        }
        .wwd-pillar-n {
          font-size: 11px;
          letter-spacing: 0.24em;
          color: var(--text-3);
        }
        .wwd-pillar-tag {
          font-size: 11px;
          letter-spacing: 0.24em;
          color: var(--ember);
          padding: 4px 10px;
          border: 1px solid var(--ember);
          border-radius: 999px;
        }
        .wwd-pillar-title {
          margin: 0 0 14px;
          font-size: clamp(22px, 2.4vw, 28px);
          line-height: 1.15;
          letter-spacing: -0.01em;
          font-weight: 500;
          color: var(--text);
        }
        .wwd-pillar-body {
          font-size: 15px;
          line-height: 1.6;
          color: var(--text-2);
          margin: 0 0 24px;
        }
        .wwd-pillar-products {
          list-style: none; padding: 0; margin: 0;
          border-top: 1px solid var(--border);
        }
        .wwd-pillar-products li { border-bottom: 1px solid var(--border); }
        .wwd-pp {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: baseline;
          gap: 14px;
          padding: 14px 0;
          text-decoration: none;
          color: var(--text);
          transition: padding .25s cubic-bezier(.2,.8,.2,1), color .25s ease;
        }
        .wwd-pp:hover { padding-left: 6px; color: var(--ember); }
        .wwd-pp-name {
          font-family: var(--f-display);
          font-size: 17px;
          letter-spacing: -0.01em;
        }
        .wwd-pp-role {
          grid-column: 1 / 2;
          font-size: 12px;
          color: var(--text-3);
          letter-spacing: 0.04em;
        }
        .wwd-pp-arrow {
          grid-row: 1 / 3;
          grid-column: 2;
          align-self: center;
          font-size: 18px;
          color: var(--text-3);
          transition: transform .25s ease, color .25s ease;
        }
        .wwd-pp:hover .wwd-pp-arrow { color: var(--ember); transform: translateX(4px); }

        /* Cognara — compact connecting block */
        .wwd-sutra {
          margin-top: 32px;
          padding: 48px;
          border: 1px solid var(--border);
          background: linear-gradient(110deg, rgba(30,78,140,0.05) 0%, transparent 60%);
          position: relative;
          overflow: hidden;
        }
        [data-theme="dark"] .wwd-sutra {
          background: linear-gradient(110deg, rgba(143,179,222,0.06) 0%, transparent 60%);
        }
        .wwd-sutra-glow {
          position: absolute;
          right: -100px; top: -100px;
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(242,107,31,0.18) 0%, transparent 60%);
          filter: blur(50px);
          animation: wwdGlow 6s ease-in-out infinite;
        }
        .wwd-sutra-mark {
          font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
          color: var(--ember); margin-bottom: 14px;
          position: relative;
        }
        .wwd-sutra-name {
          margin: 0 0 16px;
          font-size: clamp(36px, 4.2vw, 56px);
          line-height: 1;
          letter-spacing: -0.02em;
          font-weight: 500;
          position: relative;
        }
        .wwd-sutra-dev {
          font-size: 0.5em; color: var(--ember); margin-left: 14px; font-weight: 400;
        }
        .wwd-sutra-body {
          font-size: 16px; line-height: 1.65; color: var(--text-2);
          margin: 0 0 24px; max-width: 720px;
          position: relative;
        }
        .wwd-sutra-body em { font-style: italic; color: var(--text); }
        [data-theme="dark"] .wwd-sutra-body em { color: var(--steel-100); }
        .wwd-sutra-cta {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 14px; color: var(--ember); text-decoration: none;
          font-weight: 500; position: relative;
          transition: gap .25s ease;
        }
        .wwd-sutra-cta:hover { gap: 14px; }

        /* ───────── 5 · Differentiators ───────── */
        .wwd-diff-list {
          list-style: none; padding: 0; margin: 0;
          display: grid; grid-template-columns: repeat(2, 1fr);
          gap: 0;
          border-top: 1px solid var(--border);
          border-left: 1px solid var(--border);
        }
        .wwd-diff-row {
          position: relative;
          display: grid;
          grid-template-columns: 80px 1fr;
          gap: 22px;
          padding: 36px 32px;
          border-right: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          transition: background-color .3s ease;
        }
        .wwd-diff-row:hover { background: var(--bg); }
        [data-theme="dark"] .wwd-diff-row:hover { background: rgba(255,255,255,0.025); }
        .wwd-diff-n {
          font-size: clamp(40px, 5vw, 64px);
          line-height: 1; letter-spacing: -0.03em;
          color: var(--ember); opacity: 0.65;
          font-weight: 300;
          font-variant-numeric: tabular-nums;
          transition: opacity .3s ease, transform .3s ease;
        }
        .wwd-diff-row:hover .wwd-diff-n { opacity: 1; transform: translateY(-2px); }
        .wwd-diff-title {
          font-family: var(--f-display);
          font-size: 19px; line-height: 1.2;
          letter-spacing: -0.01em; margin: 0 0 10px;
          color: var(--text); font-weight: 500;
        }
        .wwd-diff-body {
          font-size: 14px; line-height: 1.6; color: var(--text-2); margin: 0;
        }
        .wwd-diff-rule {
          position: absolute; left: 0; bottom: 0;
          width: 0; height: 1px; background: var(--ember);
          transition: width .5s cubic-bezier(.2,.8,.2,1);
        }
        .wwd-diff-row:hover .wwd-diff-rule { width: 100%; }

        /* ───────── 6 · Final CTA ───────── */
        .wwd-cta {
          padding: 96px 0;
          background: #0A0E1A;
          color: #fff;
          position: relative;
          overflow: hidden;
        }
        .wwd-cta::before {
          content: ""; position: absolute;
          left: -15%; top: 30%; width: 60vw; height: 60vw;
          max-width: 800px; max-height: 800px;
          background: radial-gradient(circle, rgba(242,107,31,0.14) 0%, transparent 60%);
          filter: blur(40px); pointer-events: none;
          animation: wwdGlow 7s ease-in-out infinite;
        }
        .wwd-cta::after {
          content: ""; position: absolute;
          right: -10%; bottom: 5%; width: 50vw; height: 50vw;
          max-width: 600px; max-height: 600px;
          background: radial-gradient(circle, rgba(143,179,222,0.12) 0%, transparent 60%);
          filter: blur(50px); pointer-events: none;
        }
        .wwd-cta-inner { position: relative; max-width: 880px; margin: 0 auto; text-align: center; }
        .wwd-cta-h {
          font-size: clamp(36px, 4.6vw, 56px);
          line-height: 1.05; letter-spacing: -0.02em;
          font-weight: 400; margin: 0 0 22px; color: #fff;
        }
        .wwd-cta-h em { color: rgba(255,255,255,0.55); font-style: italic; }
        .wwd-cta-body {
          font-size: 17px; line-height: 1.55;
          color: rgba(255,255,255,0.78); max-width: 620px;
          margin: 0 auto 32px;
        }
        .wwd-cta .wwd-cta-row {
          display: flex; flex-wrap: wrap; gap: 14px;
          justify-content: center; margin-bottom: 28px;
        }
        .wwd-cta .btn-ghost {
          color: #fff; border-color: rgba(255,255,255,0.42); background: transparent;
        }
        .wwd-cta .btn-ghost:hover {
          border-color: #fff; background: rgba(255,255,255,0.06);
        }
        .wwd-cta-trust {
          font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
          color: rgba(255,255,255,0.5);
        }

        /* ───────── Animated Illustrations ─────────
           All rotations/translations are handled by SVG-native SMIL
           <animateTransform> elements inside the illustration components.
           That guarantees rotation around explicit SVG coordinates with
           zero browser inconsistency — no CSS transform-origin issues. */
        .wwd-orb, .wwd-cc { width: 100%; height: 100%; display: block; overflow: visible; }

        @keyframes wwdFadeIn  { from { opacity: 0; transform: translateY(16px) scale(.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes wwdGlow    { 0%,100% { opacity: 0.85; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }

        /* ───────── Mobile ───────── */
        @media (max-width: 980px) {
          .wwd-section { padding: 72px 0; }
          .wwd-hero { padding: 64px 0 56px; }
          .wwd-hero-inner { grid-template-columns: 1fr; gap: 32px; min-height: 0; }
          .wwd-hero-art { max-width: 380px; order: -1; }
          .wwd-industry-grid { grid-template-columns: 1fr; gap: 32px; }
          .wwd-industry-art { max-width: 440px; order: -1; }
          .wwd-stats { grid-template-columns: repeat(2, 1fr); }
          .wwd-stat:nth-child(2) { border-right: none; }
          .wwd-stat:nth-child(1), .wwd-stat:nth-child(2) { border-bottom: 1px solid var(--border); }
          .wwd-problems { grid-template-columns: 1fr; }
          .wwd-pillars { grid-template-columns: 1fr; }
          .wwd-sutra { padding: 36px 28px; }
          .wwd-diff-list { grid-template-columns: 1fr; }
        }
        @media (max-width: 540px) {
          .wwd-stats { grid-template-columns: 1fr; }
          .wwd-stat { border-right: none !important; border-bottom: 1px solid var(--border); }
          .wwd-stat:last-child { border-bottom: none; }
          .wwd-problem { padding: 24px 22px; }
          .wwd-pillar { padding: 28px 22px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .wwd-orb animate, .wwd-orb animateTransform,
          .wwd-cc animate, .wwd-cc animateTransform,
          .wwd-sutra-glow, .wwd-cta::before, .wwd-hero-art {
            animation: none !important;
          }
          .wwd-orb animate, .wwd-orb animateTransform,
          .wwd-cc animate, .wwd-cc animateTransform {
            /* SMIL respects prefers-reduced-motion natively in modern browsers,
               but we add this fallback to stop the time-based animation. */
            display: none;
          }
        }
      `}</style>
    </article>
  );
}
